import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Package,
  MapPin,
  ShoppingCart,
  Truck,
  Users,
  FileText,
  ArrowRight,
  Clock,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useGlobalSearch } from '@/hooks/useGlobalSearch';

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  icon: React.ReactNode;
  action: () => void;
  category: string;
  timestamp?: string;
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const { results, isLoading, hasResults } = useGlobalSearch(query);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      setOpen((prev) => !prev);
    }

    if (!open) return;

    switch (e.key) {
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (items[selectedIndex]) {
          items[selectedIndex].action();
          setOpen(false);
        }
        break;
    }
  }, [open, selectedIndex]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [open]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  const buildItemLink = (type: string, id: string) => {
    switch (type) {
      case 'item':
        return `/items/${id}`;
      case 'location':
        return `/locations/${id}`;
      case 'requisition':
        return `/requisitions?search=${id}`;
      case 'purchase-order':
        return `/purchase-orders?search=${id}`;
      case 'vendor':
        return `/vendors/${id}`;
      default:
        return '/';
    }
  };

  const createItem = (type: string, data: any): CommandItem => {
    const baseItem: CommandItem = {
      id: `${type}-${data.item_id || data.location_id || data.requisition_id || data.po_id || data.vendor_id || Math.random()}`,
      title: data.name || data.item_name || data.location_name || data.requisition_number || data.po_number || data.vendor_name || 'Untitled',
      description: data.code || data.item_code || '',
      icon: getIconForType(type),
      category: getCategoryLabel(type),
      action: () => {
        navigate(buildItemLink(type, data.item_id || data.location_id || data.requisition_id || data.po_id || data.vendor_id || ''));
        setOpen(false);
      },
    };
    return baseItem;
  };

  const getIconForType = (type: string) => {
    switch (type) {
      case 'item':
        return <Package className="h-4 w-4" />;
      case 'location':
        return <MapPin className="h-4 w-4" />;
      case 'requisition':
        return <ShoppingCart className="h-4 w-4" />;
      case 'purchase-order':
        return <Truck className="h-4 w-4" />;
      case 'vendor':
        return <Users className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const getCategoryLabel = (type: string) => {
    switch (type) {
      case 'item':
        return 'Items';
      case 'location':
        return 'Locations';
      case 'requisition':
        return 'Requisitions';
      case 'purchase-order':
        return 'Purchase Orders';
      case 'vendor':
        return 'Vendors';
      default:
        return 'Other';
    }
  };

  const recentSearches = React.useMemo(() => {
    try {
      const stored = localStorage.getItem('recent-searches');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }, []);

  const addToRecentSearches = (item: CommandItem) => {
    const stored = recentSearches.filter((r: CommandItem) => r.id !== item.id);
    const updated = [item, ...stored].slice(0, 5);
    localStorage.setItem('recent-searches', JSON.stringify(updated));
  };

  const items: CommandItem[] = React.useMemo(() => {
    const allItems: CommandItem[] = [];

    if (!query) {
      recentSearches.forEach((item: CommandItem) => {
        allItems.push({
          ...item,
          action: () => {
            item.action();
            setOpen(false);
          },
        });
      });

      allItems.push({
        id: 'quick-create-requisition',
        title: 'Create New Requisition',
        description: 'Start a new requisition request',
        icon: <ShoppingCart className="h-4 w-4" />,
        category: 'Actions',
        action: () => {
          navigate('/requisitions?action=new');
          setOpen(false);
        },
      });

      allItems.push({
        id: 'quick-create-po',
        title: 'Create Purchase Order',
        description: 'Create a new purchase order',
        icon: <Truck className="h-4 w-4" />,
        category: 'Actions',
        action: () => {
          navigate('/purchase-orders?action=new');
          setOpen(false);
        },
      });

      allItems.push({
        id: 'quick-receive-goods',
        title: 'Receive Goods',
        description: 'Receive inventory from purchase orders',
        icon: <Package className="h-4 w-4" />,
        category: 'Actions',
        action: () => {
          navigate('/receive-goods');
          setOpen(false);
        },
      });

      allItems.push({
        id: 'quick-transfer',
        title: 'Transfer Stock',
        description: 'Move inventory between locations',
        icon: <MapPin className="h-4 w-4" />,
        category: 'Actions',
        action: () => {
          navigate('/stock-transfers?action=new');
          setOpen(false);
        },
      });

      return allItems;
    }

    results.items.forEach((item) => {
      allItems.push(createItem('item', item));
    });
    results.locations.forEach((location) => {
      allItems.push(createItem('location', location));
    });
    results.requisitions.forEach((req) => {
      allItems.push(createItem('requisition', req));
    });
    results.purchaseOrders.forEach((po) => {
      allItems.push(createItem('purchase-order', po));
    });
    results.vendors.forEach((vendor) => {
      allItems.push(createItem('vendor', vendor));
    });

    return allItems;
  }, [query, results, navigate, recentSearches]);

  const groupedItems = React.useMemo(() => {
    const groups: Record<string, CommandItem[]> = {};
    items.forEach((item) => {
      if (!groups[item.category]) {
        groups[item.category] = [];
      }
      groups[item.category].push(item);
    });
    return groups;
  }, [items]);

  let flatIndex = -1;

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-full justify-start rounded-md px-3 text-left text-sm sm:pr-12 md:w-64 lg:w-80"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="hidden lg:inline-flex">Search...</span>
        <span className="inline-flex lg:hidden">Search...</span>
        <kbd className="pointer-events-none absolute right-1.5 top-1.5 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </kbd>
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="relative w-full max-w-lg overflow-hidden rounded-xl border bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center border-b px-4">
              <Search className="mr-2 h-5 w-5 shrink-0 text-gray-500" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search items, locations, requisitions..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="flex h-12 w-full rounded-md bg-transparent py-3 text-sm outline-none placeholder:text-gray-500 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-100"
              />
              <button
                onClick={() => setOpen(false)}
                className="rounded-sm px-2 py-1 text-sm text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-2">
              {isLoading ? (
                <div className="space-y-2 p-4">
                  {[...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="h-12 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800"
                    />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800">
                    <Search className="h-6 w-6 text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    No results found
                  </p>
                  <p className="text-sm text-gray-500">
                    Try searching for something else
                  </p>
                </div>
              ) : (
                Object.entries(groupedItems).map(([category, categoryItems]) => (
                  <div key={category} className="mb-2">
                    <div className="px-2 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {category}
                    </div>
                    {categoryItems.map((item) => {
                      flatIndex++;
                      const currentIndex = flatIndex;
                      return (
                        <button
                          key={item.id}
                          onClick={() => {
                            if (!query) {
                              addToRecentSearches(item);
                            }
                            item.action();
                          }}
                          className={cn(
                            'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors',
                            currentIndex === selectedIndex
                              ? 'bg-gray-100 dark:bg-gray-800'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                          )}
                        >
                          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800">
                            {item.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                              {item.title}
                            </p>
                            {item.description && (
                              <p className="truncate text-xs text-gray-500">
                                {item.description}
                              </p>
                            )}
                          </div>
                          <ArrowRight className="h-4 w-4 text-gray-400" />
                        </button>
                      );
                    })}
                  </div>
                ))
              )}
            </div>

            <div className="border-t bg-gray-50 px-4 py-2 dark:bg-gray-800/50">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-700">↑</kbd>
                    <kbd className="rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-700">↓</kbd>
                    <span>Navigate</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-700">↵</kbd>
                    <span>Select</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="rounded bg-gray-200 px-1.5 py-0.5 dark:bg-gray-700">esc</kbd>
                    <span>Close</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
